import { OrderServiceTypeStore } from '../../services/order-service-type-store.service';
import { TimelineService } from '../../services/timeline.service';
import { EServiceType } from '@food-certification-app/app/models/enums/service-type.enum';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

/**
 * Ensures {@link OrderServiceTypeStore} has service type for the order before entering
 * `order/:orderNumber` or any child route; otherwise loads it via {@link TimelineService.getOrderStatus}.
 */
@Injectable({
  providedIn: 'root',
})
export class OrderServiceTypeGuard implements CanActivate, CanActivateChild {
  public constructor(
    private readonly _orderServiceTypeStore: OrderServiceTypeStore,
    private readonly _timelineService: TimelineService,
    private readonly _router: Router
  ) {}

  public canActivate(route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this._ensureOrderServiceType$(route);
  }

  public canActivateChild(childRoute: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this._ensureOrderServiceType$(childRoute);
  }

  private _ensureOrderServiceType$(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    const orderNumber = this._getOrderNumberFromRoute(route);

    if (!orderNumber) {
      return of(this._router.parseUrl('/home'));
    }

    if (this._orderServiceTypeStore.hasStoredOrderServiceType(orderNumber)) {
      return of(true);
    }

    return this._timelineService.getOrderStatus(orderNumber).pipe(
      tap((response): void => {
        this._orderServiceTypeStore.setOrderServiceType(orderNumber, response.serviceType || EServiceType.BRCGS);
      }),
      map((): boolean => true)
    );
  }

  private _getOrderNumberFromRoute(route: ActivatedRouteSnapshot): string | null {
    let current: ActivatedRouteSnapshot | null = route;

    while (current) {
      const id = current.paramMap.get('orderNumber');

      if (id) {
        return id;
      }

      current = current.parent;
    }

    return null;
  }
}
